/**
 * Telling a YouTube write failure that will never succeed from one that will.
 *
 * This distinction is the whole point. A failed push is deliberately left out
 * of the sync snapshot so it is attempted again next time, which is right for
 * a quota limit and wrong for a video that has been deleted: that one is
 * retried on every sync forever, costing 50 quota units and a Sentry error
 * each time, and it can never work. Permanent failures are recorded on the
 * sync source instead and stop being attempted.
 */

/** Reasons that mean "this will not work, ever" regardless of status code. */
const permanentReasons = [
  // The video is gone, private, or the id is not a video at all.
  'videoNotFound',
  'invalidVideoId',
  // The playlist cannot be modified through the API - Watch Later and
  // Watch History both answer this way.
  'playlistOperationUnsupported',
  'playlistItemsNotAccessible',
];

/** Reasons that are explicitly worth another attempt later. */
const transientReasons = [
  'quotaExceeded',
  'dailyLimitExceeded',
  'rateLimitExceeded',
  'userRateLimitExceeded',
  'backendError',
  'internalError',
  'serviceUnavailable',
  // Auth trouble: the token lapsed, and signing in again fixes it.
  'authError',
  'forbidden',
];

/** A delete that reports this has already reached the state we wanted. */
export const playlistItemNotFound = 'playlistItemNotFound';

export type WriteOperation = 'add' | 'remove';

export interface WriteFailure {
  videoId: string;
  operation: WriteOperation;
  /** The API's reason string, or the closest thing to one. */
  reason: string;
  /** Retrying will never help. Record it and stop trying. */
  permanent: boolean;
  /**
   * A removal that found nothing to remove. Not a failure at all - the video
   * is not in the playlist, which is exactly what was asked for.
   */
  alreadyGone: boolean;
}

export function classifyWriteFailure(
  err: any,
  operation: WriteOperation,
): { reason: string; permanent: boolean; alreadyGone: boolean } {
  const apiError = err?.result?.error;

  if (!apiError) {
    // No Google error body: a network failure, an aborted request, or one of
    // our own guards rejecting with a string. None of those say anything
    // about the video, so never give up on the strength of one.
    return {
      reason: typeof err === 'string' ? err : err?.message || 'Unknown error',
      permanent: false,
      alreadyGone: false,
    };
  }

  const reasons: string[] = (apiError.errors || []).map((x: any) => x && x.reason).filter(Boolean);
  const reason = reasons[0] || apiError.message || `HTTP ${apiError.code}`;

  if (operation === 'remove' && reasons.indexOf(playlistItemNotFound) !== -1) {
    return { reason, permanent: false, alreadyGone: true };
  }

  if (reasons.some((x) => permanentReasons.indexOf(x) !== -1)) {
    return { reason, permanent: true, alreadyGone: false };
  }

  if (reasons.some((x) => transientReasons.indexOf(x) !== -1)) {
    return { reason, permanent: false, alreadyGone: false };
  }

  // An unfamiliar reason. Fall back to the status code, and lean towards
  // retrying: wrongly calling something permanent silently drops a track out
  // of the sync, while wrongly retrying costs quota and shows up in Sentry.
  // Only "not found" and "malformed request" are hopeless on their face.
  return {
    reason,
    permanent: apiError.code === 404 || apiError.code === 400,
    alreadyGone: false,
  };
}
